import { useCallback, useEffect, useState } from 'react';
import { Drawer, List, Tag, Empty, Spin, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../store/store';
import { NavbarButton } from '../components/Layout/NavbarButton';
import { iconButtonStyle } from '../utils/styles';
import { getVersions, getDocumentContent } from '../requests/versions';
import { reasonLabel, reasonColor } from './reasons';
import { formatDate } from './format';
import { VersionDetail } from './VersionDetail';

// Cible le document courant comme le fait persistence.js : un template est
// adressé par currentDocument.id, sinon par currentProposal.id.
const selector = (state) => ({
  documentType:
    state.currentDocument?.type === 'template' ? 'template' : 'proposal',
  documentId:
    state.currentDocument?.type === 'template'
      ? state.currentDocument?.id
      : state.currentProposal?.id,
  proposalList: state.proposalList,
});

// Panneau d'historique des états du content : bouton dans la navbar ouvrant un
// Drawer qui liste les versions (métadonnées) du document courant, et permet
// d'ouvrir le détail d'une version (changements vs la précédente).
export const HistoryPanel = () => {
  const { documentType, documentId, proposalList } = useStore(selector);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  // Content actuel persisté du doc, pour diffé chaque version contre l'état courant.
  const [currentContent, setCurrentContent] = useState(null);
  // La version dont on regarde le détail.
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setSelected(null);
    try {
      const [data, content] = await Promise.all([
        getVersions({ documentType, documentId }),
        getDocumentContent({ documentType, documentId }),
      ]);
      setVersions(Array.isArray(data) ? data : []);
      setCurrentContent(content ?? null);
    } catch (error) {
      console.error('Chargement de l’historique échoué', error);
      message.error({
        content: 'Impossible de charger l’historique',
        key: 'history-load-error',
      });
    } finally {
      setLoading(false);
    }
  }, [documentType, documentId]);

  const openDrawer = useCallback(() => setOpen(true), []);

  // Recharge à l'ouverture et réinitialise au changement de document : sans ça,
  // changer de doc avec le drawer ouvert laisse l'historique du doc précédent.
  useEffect(() => {
    setSelected(null);
    setVersions([]);
    setCurrentContent(null);
    if (open && documentId) load();
  }, [open, documentType, documentId, load]);

  // Pas de bouton sur la liste des documents ou sans document ouvert.
  if (proposalList || !documentId) return null;

  return (
    <>
      <NavbarButton
        tooltipContent='Historique du document'
        onClick={openDrawer}
        icon={
          <FontAwesomeIcon
            icon={icon({ name: 'clock-rotate-left', family: 'duotone', style: 'solid' })}
            style={iconButtonStyle()}
          />
        }
      />
      <Drawer
        title='Historique du document'
        placement='right'
        width={360}
        open={open}
        onClose={() => setOpen(false)}
        closable
        closeIcon={
          <FontAwesomeIcon icon={icon({ name: 'xmark', family: 'duotone', style: 'solid' })} />
        }
        // La navbar est fixed à zIndex 1100 (Navbar.jsx) : sans ça le header du
        // Drawer (et sa croix) passe derrière la navbar.
        zIndex={1200}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : selected ? (
          <VersionDetail
            documentType={documentType}
            documentId={documentId}
            version={selected}
            currentContent={currentContent}
            onBack={() => setSelected(null)}
            onRestored={load}
            onRenamed={(id, label) => {
              setVersions((prev) =>
                prev.map((v) => (v.id === id ? { ...v, label } : v))
              );
              setSelected((prev) =>
                prev && prev.id === id ? { ...prev, label } : prev
              );
            }}
          />
        ) : versions.length === 0 ? (
          <Empty description='Aucune version enregistrée' />
        ) : (
          <List
            dataSource={versions}
            rowKey='id'
            renderItem={(version) => (
              <List.Item
                onClick={() => setSelected(version)}
                style={{ cursor: 'pointer' }}
              >
                <List.Item.Meta
                  title={
                    <Tag color={reasonColor(version.reason)}>
                      {reasonLabel(version.reason)}
                    </Tag>
                  }
                  description={
                    <>
                      {version.label ? (
                        <div style={{ fontSize: 13, color: '#555' }}>
                          {version.label}
                        </div>
                      ) : null}
                      <div>{formatDate(version.created_at)}</div>
                      {version.author_name ? (
                        <div style={{ color: '#888' }}>
                          {version.author_name}
                        </div>
                      ) : null}
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </>
  );
};

export default HistoryPanel;
